import type { UserLocation } from '@/firebase/types';

/** Map shell coordination events (formerly scattered `window.CustomEvent` strings). */
export const MAP_EVENTS = {
  clearSelectedLocation: 'clearSelectedLocation',
  clearMapState: 'clearMapState',
  openLocationDetails: 'openLocationDetails',
  openCatalogPoi: 'openCatalogPoi',
  openV1Places: 'openV1Places',
  closeUserMenu: 'closeUserMenu',
  clearMapToolbarSearch: 'clearMapToolbarSearch',
  closeMapShellLeftMenu: 'closeMapShellLeftMenu',
  showAllUserLocationsOnMap: 'showAllUserLocationsOnMap',
  saveLocationFormOpening: 'saveLocationFormOpening',
  saveLocationFormOpened: 'saveLocationFormOpened',
  zoomToLocation: 'zoomToLocation',
  openUserLocationMobileTooltip: 'openUserLocationMobileTooltip',
  selectLocationInUserMenu: 'selectLocationInUserMenu',
} as const;

type MapEventType = (typeof MAP_EVENTS)[keyof typeof MAP_EVENTS];

interface MapEventPayloads {
  clearSelectedLocation: undefined;
  clearMapState: undefined;
  openLocationDetails: { location?: UserLocation };
  openCatalogPoi: undefined;
  openV1Places: undefined;
  closeUserMenu: undefined;
  clearMapToolbarSearch: undefined;
  closeMapShellLeftMenu: undefined;
  showAllUserLocationsOnMap: undefined;
  saveLocationFormOpening: undefined;
  saveLocationFormOpened: undefined;
  zoomToLocation: { location: UserLocation };
  openUserLocationMobileTooltip: { location: UserLocation };
  selectLocationInUserMenu: { location: UserLocation };
}

type MapEventDetail<T extends MapEventType> = MapEventPayloads[T];

export function dispatchMapEvent<T extends MapEventType>(
  type: T,
  ...args: MapEventDetail<T> extends undefined ? [] : [MapEventDetail<T>]
): void {
  const detail = args[0];
  if (detail === undefined) {
    window.dispatchEvent(new CustomEvent(type));
  } else {
    window.dispatchEvent(new CustomEvent(type, { detail }));
  }
}

export function subscribeMapEvent<T extends MapEventType>(
  type: T,
  handler: MapEventDetail<T> extends undefined
    ? () => void
    : (detail: MapEventDetail<T>) => void,
): () => void {
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<MapEventDetail<T>>).detail;
    if (detail === undefined) {
      (handler as () => void)();
    } else {
      (handler as (payload: MapEventDetail<T>) => void)(detail);
    }
  };
  window.addEventListener(type, listener);
  return () => window.removeEventListener(type, listener);
}
